import { useQuery } from "@tanstack/react-query";
import { useApi } from "@ncp/api-client";
import { useLocale } from "@ncp/i18n";

/** A provision a screen names, as the legal rules register cites it, and where the Department's verification of it stands. */
export interface ProvisionTerm { key: string; cited: string; citedAr: string; status: string }

/**
 * The periods and provisions the request, Deal Watch and access-to-file screens name (GET /api/public/procedure-terms; conditions 2.13 and 2.14):
 * each period from its one definition, each provision cited from the legal rules register.
 */
export interface ProcedureTerms {
  requestDataWorkingDays: number;
  opinionMonths: number;
  opinionPeriod: string;
  opinionPeriodAr: string;
  dealWatchNoticeDays: number;
  fileAccessDefaultWorkingDays: number;
  fileAccessMinWorkingDays: number;
  fileAccessMaxWorkingDays: number;
  provisions: Record<"opinion" | "opinionContent" | "marketData" | "notification" | "failureToNotify" | "implementationBeforeClearance", ProvisionTerm>;
}

/** The terms change only with a release: one fetch per session. */
export function useProcedureTerms() {
  const api = useApi();
  return useQuery({ queryKey: ["public", "procedure-terms"], queryFn: () => api.get<ProcedureTerms>("/api/public/procedure-terms"), staleTime: Infinity });
}

/**
 * The words a sentence names them in, in the reader's language – `{{days}}`, `{{opinionPeriod}}`, `{{opinionBasis}}`, `{{contentBasis}}`,
 * `{{marketDataBasis}}` of a locale string. Dashes until the terms have loaded: a text never states a period or an article of its own.
 */
export function useProcedureTermWords() {
  const { locale } = useLocale();
  const terms = useProcedureTerms().data;
  const ar = locale === "ar";
  const cite = (p: ProvisionTerm | undefined) => (p ? (ar ? p.citedAr : p.cited) : "—");
  return {
    days: terms ? String(terms.requestDataWorkingDays) : "—",
    opinionPeriod: terms ? (ar ? terms.opinionPeriodAr : terms.opinionPeriod) : "—",
    opinionBasis: cite(terms?.provisions.opinion),
    contentBasis: cite(terms?.provisions.opinionContent),
    marketDataBasis: cite(terms?.provisions.marketData),
  };
}
